/**
 * View Health Record Page - Display detailed view of a specific health record
 * Mapped to backend HealthRecord with exact field names
 * Uses same layout and styling as ViewFoodHistory for consistency
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Loader2 } from 'lucide-react';
import { healthRecordsApi, cattleApi, type HealthRecord, type Cattle } from '../../services/gaushala/api';
import { toast } from 'sonner';

export default function ViewHealthRecord() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [record, setRecord] = useState<HealthRecord | null>(null);
  const [cattle, setCattle] = useState<Cattle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecord = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const result = await healthRecordsApi.getHealthRecordById(parseInt(id));

        console.log('Health Record API Result:', result);

        if (result.success && result.data) {
          console.log('Health Record from API:', result.data);
          setRecord(result.data);

          // Fetch cattle details if cattleId is available
          if (result.data.cattleId) {
            try {
              const cattleResult = await cattleApi.getCattleById(result.data.cattleId);
              if (cattleResult.success && cattleResult.data) {
                setCattle(cattleResult.data);
              }
            } catch (cattleError) {
              console.error('Error fetching cattle details:', cattleError);
              // Don't fail the whole component if cattle fetch fails
            }
          }
        } else {
          setError(result.error || 'Health record not found');
          toast.error(result.error || 'Health record not found');
        }
      } catch (error) {
        console.error('Error fetching health record:', error);
        setError('Failed to load health record details');
        toast.error('Failed to load health record details');
      } finally {
        setLoading(false);
      }
    };

    fetchRecord();
  }, [id]);

  const handleEdit = () => {
    navigate(`/gaushala/health-history/edit/${id}`);
  };

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this health record?')) {
      return;
    }

    try {
      const result = await healthRecordsApi.deleteHealthRecord(parseInt(id));

      if (result.success) {
        toast.success('Health record deleted successfully');
        navigate('/gaushala/health-history');
      } else {
        toast.error(result.error || 'Failed to delete health record');
      }
    } catch (error) {
      console.error('Error deleting health record:', error);
      toast.error('Failed to delete health record');
    }
  };

  const handleBack = () => {
    navigate('/gaushala/health-history');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getRecordTypeBadgeColor = (type?: string) => {
    switch (type) {
      case 'VACCINATION':
        return 'bg-purple-100 text-purple-800';
      case 'TREATMENT':
        return 'bg-orange-100 text-orange-800';
      case 'CHECKUP':
        return 'bg-blue-100 text-blue-800';
      case 'SURGERY':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status?: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'SCHEDULED':
        return 'bg-blue-100 text-blue-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading health record...</p>
        </div>
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Health Record Not Found</h1>
            <p className="text-gray-600 mt-1">{error || 'The requested health record could not be found.'}</p>
          </div>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Health Record #{record.id}</h1>
          <p className="text-gray-600 mt-1">
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getRecordTypeBadgeColor(record.recordType)}`}>
              {record.recordType || 'Unknown'}
            </span>
            {record.status && (
              <span className={`ml-2 inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(record.status)}`}>
                {record.status}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={handleEdit}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cattle Information */}
            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cattle Name</p>
              <p className="text-base font-medium text-gray-900">{cattle?.name || `Cattle #${record.cattleId}` || '-'}</p>
            </div>

            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Animal ID</p>
              <p className="text-base text-gray-900">{cattle?.uniqueAnimalId || `ID: ${record.cattleId}` || '-'}</p>
            </div>

            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Breed</p>
              <p className="text-base text-gray-900">{cattle?.breedName || '-'}</p>
            </div>

            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Gender</p>
              <p className="text-base text-gray-900">{cattle?.gender || '-'}</p>
            </div>

            {/* Record Type and Status */}
            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Record Type</p>
              <p className="text-base text-gray-900">
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getRecordTypeBadgeColor(record.recordType)}`}>
                  {record.recordType || '-'}
                </span>
              </p>
            </div>

            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</p>
              <p className="text-base text-gray-900">
                {record.status ? (
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(record.status)}`}>
                    {record.status}
                  </span>
                ) : (
                  '-'
                )}
              </p>
            </div>

            {/* Date Information */}
            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Record Date</p>
              <p className="text-base text-gray-900">{record.recordDate ? formatDate(record.recordDate) : '-'}</p>
            </div>

            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Next Vaccination Date</p>
              <p className="text-base text-gray-900">{record.nextVaccinationDate ? formatDate(record.nextVaccinationDate) : '-'}</p>
            </div>

            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Next Checkup Date</p>
              <p className="text-base text-gray-900">{record.nextCheckupDate ? formatDate(record.nextCheckupDate) : '-'}</p>
            </div>

            {/* Veterinarian Information */}
            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Veterinarian Name</p>
              <p className="text-base font-medium text-gray-900">{record.veterinarianName || '-'}</p>
            </div>

            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Veterinarian License</p>
              <p className="text-base text-gray-900">{record.veterinarianLicense || '-'}</p>
            </div>

            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Veterinarian Contact</p>
              <p className="text-base text-gray-900">{record.veterinarianContact || '-'}</p>
            </div>

            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Performed By</p>
              <p className="text-base text-gray-900">{record.performedBy || '-'}</p>
            </div>

            {/* Vaccination Information */}
            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Vaccination Type</p>
              <p className="text-base text-gray-900">{record.vaccinationType || '-'}</p>
            </div>

            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cost</p>
              <p className="text-base font-medium text-gray-900">{formatCurrency(record.cost)}</p>
            </div>

            {/* Diagnosis and Treatment */}
            <div className="md:col-span-2 space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Diagnosis</p>
              {record.diagnosis ? (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">{record.diagnosis}</p>
                </div>
              ) : (
                <p className="text-gray-900">-</p>
              )}
            </div>

            <div className="md:col-span-2 space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Treatment</p>
              {record.treatment ? (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">{record.treatment}</p>
                </div>
              ) : (
                <p className="text-gray-900">-</p>
              )}
            </div>

            {/* Medications */}
            <div className="md:col-span-2 space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Medications</p>
              {record.medications ? (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">{record.medications}</p>
                </div>
              ) : (
                <p className="text-gray-900">-</p>
              )}
            </div>

            <div className="md:col-span-2 space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Dosage Instructions</p>
              {record.dosageInstructions ? (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">{record.dosageInstructions}</p>
                </div>
              ) : (
                <p className="text-gray-900">-</p>
              )}
            </div>

            {/* Notes */}
            <div className="md:col-span-2 space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Notes & Additional Information</p>
              {record.notes ? (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">{record.notes}</p>
                </div>
              ) : (
                <p className="text-gray-900">-</p>
              )}
            </div>

            {/* Record Information & Audit Trail */}
            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Record ID</p>
              <p className="text-base text-gray-900 font-mono text-sm">{record.id || '-'}</p>
            </div>

            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Created At</p>
              <p className="text-base text-gray-900">{record.createdAt ? formatDate(record.createdAt) : '-'}</p>
            </div>

            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Last Updated</p>
              <p className="text-base text-gray-900">{record.updatedAt ? formatDate(record.updatedAt) : '-'}</p>
            </div>

            <div className="space-y-2 pb-4 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Created By</p>
              <p className="text-base text-gray-900">{record.createdBy ? `User #${record.createdBy}` : '-'}</p>
            </div>

            <div className="space-y-2 pb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Updated By</p>
              <p className="text-base text-gray-900">{record.updatedBy ? `User #${record.updatedBy}` : '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 flex-wrap">
        <button
          onClick={handleEdit}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Edit className="h-4 w-4" />
          Edit Record
        </button>
        <button
          onClick={handleBack}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to List
        </button>
      </div>
    </div>
  );
}
